import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import PageHeader from '../../common/PageHeader';
import {
  fetchIndicatorsDebtsToPay,
  fetchIndicatorsPropertyDebtsToPay,
  fetchIndicatorsPropertyPayments,
  fetchIndicatorsReport,
  fetchIndicatorsReservations,
  fetchIndicatorsVisitors,
} from '../../../helpers/api/finance';
import IndicatorsStats from './components/IndicatorsStats';
import IndicatorsTable from './components/IndicatorsTable';
import IndicatorsPeriodFilter from './components/IndicatorsPeriodFilter';
import IndicatorsAdoption from './adoption/IndicatorsAdoption';

const METRIC_LABELS = {
  debtsToPay: 'Cuentas por pagar',
  receivables: 'Efectos por cobrar',
  ownerPayments: 'Pagos de propietarios',
  visitors: 'Visitantes',
  commonAreaReservations: 'Reservas de áreas comunes',
};

const METRIC_FETCHERS = {
  debtsToPay: fetchIndicatorsDebtsToPay,
  receivables: fetchIndicatorsPropertyDebtsToPay,
  ownerPayments: fetchIndicatorsPropertyPayments,
  visitors: fetchIndicatorsVisitors,
  commonAreaReservations: fetchIndicatorsReservations,
};

const Indicators = () => {
  const { token } = useSelector((state) => state.auth.user);
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const initialGlobalRange = { since: moment().startOf('month'), to: moment() };

  const [selectedMetric, setSelectedMetric] = useState(null);
  const [globalPeriod, setGlobalPeriod] = useState('current_month');
  const [globalDateRange, setGlobalDateRange] = useState(initialGlobalRange);
  const [globalCustomDateRange, setGlobalCustomDateRange] = useState(initialGlobalRange);

  const [textSearch, setTextSearch] = useState('');
  const [recordFilter, setRecordFilter] = useState('with');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [tableDateRange, setTableDateRange] = useState(initialGlobalRange);
  const [tableRangeSource, setTableRangeSource] = useState('inherited');
  const [tableDateValidationMessage, setTableDateValidationMessage] = useState('');
  const [tableRows, setTableRows] = useState([]);
  const [tableError, setTableError] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableMeta, setTableMeta] = useState({
    total: 0,
    currentPage: 1,
    lastPage: 1,
    perPage: 5,
    to: null,
    from: null,
  });
  const [totalsByCondominium, setTotalsByCondominium] = useState({});
  const [queryParams, setQueryParams] = useState({
    search: null,
    start_date: initialGlobalRange.since.format('YYYY-MM-DD'),
    end_date: initialGlobalRange.to.format('YYYY-MM-DD'),
    has_records: 'with',
    page: 1,
    limit: 5,
  });

  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);
  const [statsEmpty, setStatsEmpty] = useState(false);
  const [totals, setTotals] = useState({
    debtsToPay: 0,
    receivables: 0,
    ownerPayments: 0,
    visitors: 0,
    commonAreaReservations: 0,
  });

  const formatDateParam = (date) => (date ? date.format('YYYY-MM-DD') : null);
  const formatDateDisplay = (date) => (date ? date.format('DD/MM/YYYY') : 'sin fecha');

  const globalSinceKey = globalDateRange.since ? globalDateRange.since.format('YYYY-MM-DD') : '';
  const globalToKey = globalDateRange.to ? globalDateRange.to.format('YYYY-MM-DD') : '';

  useEffect(() => {
    if (globalPeriod === 'current_month') {
      setGlobalDateRange({ since: moment().startOf('month'), to: moment() });
    }

    if (globalPeriod === 'current_year') {
      setGlobalDateRange({ since: moment().startOf('year'), to: moment() });
    }
  }, [globalPeriod]);

  useEffect(() => {
    if (globalPeriod !== 'custom') {
      setGlobalCustomDateRange({
        since: globalDateRange.since ? globalDateRange.since.clone() : null,
        to: globalDateRange.to ? globalDateRange.to.clone() : null,
      });
    }
  }, [globalPeriod, globalSinceKey, globalToKey]);

  useEffect(() => {
    setTableDateRange({
      since: globalDateRange.since ? globalDateRange.since.clone() : null,
      to: globalDateRange.to ? globalDateRange.to.clone() : null,
    });
    setTableRangeSource('inherited');
    setQueryParams((prev) => ({
      ...prev,
      start_date: formatDateParam(globalDateRange.since),
      end_date: formatDateParam(globalDateRange.to),
      page: 1,
    }));
  }, [selectedMetric, globalSinceKey, globalToKey]);

  useEffect(() => {
    if (!selectedMetric) {
      return;
    }

    setTextSearch('');
    setRecordFilter('with');
    setShowDateFilter(false);
    setTableDateValidationMessage('');
    setQueryParams((prev) => ({
      ...prev,
      search: null,
      has_records: 'with',
      page: 1,
    }));
  }, [selectedMetric]);

  const applyTextSearch = (value = textSearch) => {
    const term = typeof value === "string" ? value.trim() : "";
    setTextSearch(term);
    setQueryParams((prev) => ({
      ...prev,
      search: term || null,
      page: 1,
    }));
  };

  useEffect(() => {
    if (!selectedMetric) {
      return;
    }

    setQueryParams((prev) => ({
      ...prev,
      has_records: recordFilter === 'all' ? null : recordFilter,
      page: 1,
    }));
  }, [recordFilter, selectedMetric]);

  useEffect(() => {
    let isMounted = true;

    const loadIndicators = async () => {
      setStatsLoading(true);
      setStatsError(false);

      try {
        const response = await fetchIndicatorsReport({
          condominium_id: selectedCondominium || null,
          start_date: formatDateParam(globalDateRange.since),
          end_date: formatDateParam(globalDateRange.to),
        });

        const payload = response?.data && typeof response.data === 'object' ? response.data : response;

        const mappedTotals = {
          debtsToPay: Number(payload?.condominiums_with_debts_to_pay) || 0,
          receivables: Number(payload?.condominiums_with_property_debts_to_pay) || 0,
          ownerPayments: Number(payload?.condominiums_with_property_payments) || 0,
          visitors: Number(payload?.condominiums_with_visitors) || 0,
          commonAreaReservations: Number(payload?.condominiums_with_reservations) || 0,
        };

        const hasAnyValue = Object.values(mappedTotals).some((value) => value > 0);

        if (isMounted) {
          setTotals(mappedTotals);
          setStatsEmpty(!hasAnyValue);
        }
      } catch (error) {
        if (isMounted) {
          setTotals({
            debtsToPay: 0,
            receivables: 0,
            ownerPayments: 0,
            visitors: 0,
            commonAreaReservations: 0,
          });
          setStatsError(true);
          setStatsEmpty(false);
        }
      } finally {
        if (isMounted) {
          setStatsLoading(false);
        }
      }
    };

    loadIndicators();

    return () => {
      isMounted = false;
    };
  }, [token, selectedCondominium, globalSinceKey, globalToKey]);

  useEffect(() => {
    if (!selectedMetric) {
      setTableRows([]);
      setTableError(null);
      setTableLoading(false);
      setTotalsByCondominium({});
      return;
    }

    if (
      queryParams.start_date
      && queryParams.end_date
      && moment(queryParams.start_date).isAfter(moment(queryParams.end_date))
    ) {
      setTableDateValidationMessage('La fecha "Desde" no puede ser mayor que "Hasta".');
      setTableRows([]);
      setTotalsByCondominium({});
      setTableError(null);
      setTableLoading(false);
      return;
    }

    let isMounted = true;
    const fetchMetricData = METRIC_FETCHERS[selectedMetric];

    const loadTableData = async () => {
      setTableLoading(true);
      setTableError(null);

      try {
        const response = await fetchMetricData(queryParams.page, queryParams.limit, {
          condominium_id: selectedCondominium || null,
          search: queryParams.search,
          start_date: queryParams.start_date,
          end_date: queryParams.end_date,
          has_records: queryParams.has_records,
        });

        const rows = Array.isArray(response?.data) ? response.data : [];
        const sortedRows = [...rows].sort((a, b) => (Number(b?.totalCounts) || 0) - (Number(a?.totalCounts) || 0));
        const totalsMap = sortedRows.reduce((acc, row) => {
          const key = row?.condominium_id || row?.id;
          if (key) {
            acc[key] = Number(row?.totalCounts) || 0;
          }
          return acc;
        }, {});

        if (isMounted) {
          setTableRows(sortedRows);
          setTotalsByCondominium(totalsMap);
          setTableMeta({
            total: response?.meta?.total ?? sortedRows.length,
            currentPage: response?.meta?.current_page ?? queryParams.page,
            lastPage: response?.meta?.last_page ?? 1,
            perPage: response?.meta?.per_page ?? queryParams.limit,
            to: response?.meta?.to ?? null,
            from: response?.meta?.from ?? null,
          });
        }
      } catch (error) {
        if (isMounted) {
          setTableRows([]);
          setTotalsByCondominium({});
          setTableError(error);
        }
      } finally {
        if (isMounted) {
          setTableLoading(false);
        }
      }
    };

    loadTableData();

    return () => {
      isMounted = false;
    };
  }, [
    selectedMetric,
    selectedCondominium,
    queryParams.page,
    queryParams.limit,
    queryParams.search,
    queryParams.start_date,
    queryParams.end_date,
    queryParams.has_records,
  ]);

  const applyFilters = (range = tableDateRange) => {
    if (range.since && range.to && range.since.isAfter(range.to)) {
      setTableDateValidationMessage('La fecha "Desde" no puede ser mayor que "Hasta".');
      return;
    }

    setTableDateValidationMessage('');
    setQueryParams((prev) => ({
      ...prev,
      search: textSearch.trim() ? textSearch.trim() : null,
      start_date: formatDateParam(range.since),
      end_date: formatDateParam(range.to),
      page: 1,
    }));
  };

  const resetTableDateFromGlobal = () => {
    const inheritedRange = {
      since: globalDateRange.since ? globalDateRange.since.clone() : null,
      to: globalDateRange.to ? globalDateRange.to.clone() : null,
    };

    setTableDateRange(inheritedRange);
    setTableRangeSource('inherited');
    setTableDateValidationMessage('');
    setQueryParams((prev) => ({
      ...prev,
      start_date: formatDateParam(inheritedRange.since),
      end_date: formatDateParam(inheritedRange.to),
      page: 1,
    }));
  };

  const tableData = !selectedMetric
    ? []
    : tableRows;

  const scopeBannerText = selectedMetric
    ? `Mostrando condominios con ${METRIC_LABELS[selectedMetric]} para el periodo del ${formatDateDisplay(tableDateRange.since)} al ${formatDateDisplay(tableDateRange.to)}`
    : '';

  const handleGlobalPeriodChange = (period) => {
    if (period === 'custom') {
      setGlobalCustomDateRange({
        since: globalDateRange.since ? globalDateRange.since.clone() : null,
        to: globalDateRange.to ? globalDateRange.to.clone() : null,
      });
    }

    setGlobalPeriod(period);
  };

  const handleGlobalDateRangeChange = (field, value) => {
    setGlobalCustomDateRange((prev) => ({ ...prev, [field]: value }));
  };

  const applyGlobalCustomDateRange = () => {
    setGlobalDateRange({
      since: globalCustomDateRange.since ? globalCustomDateRange.since.clone() : null,
      to: globalCustomDateRange.to ? globalCustomDateRange.to.clone() : null,
    });
  };

  const handleTableDateRangeChange = (field, value) => {
    setTableRangeSource('custom');
    setTableDateValidationMessage('');
    setTableDateRange((prev) => ({ ...prev, [field]: value }));
  };

  const nextPage = () => {
    setQueryParams((prev) => ({
      ...prev,
      page: prev.page < tableMeta.lastPage ? prev.page + 1 : prev.page,
    }));
  };

  const previousPage = () => {
    setQueryParams((prev) => ({
      ...prev,
      page: prev.page > 1 ? prev.page - 1 : 1,
    }));
  };

  const goToPage = (pageNumber) => {
    setQueryParams((prev) => ({
      ...prev,
      page: pageNumber,
    }));
  };

  const perPage = (perPageNumber) => {
    setQueryParams((prev) => ({
      ...prev,
      limit: perPageNumber,
      page: 1,
    }));
  };

  return (
    <>
      <PageHeader
        title="Indicadores"
        titleTag="h2"
        description="Adopción de Homelan en los condominios registrados y actividad por módulo."
        className="mb-3"
        col={{ lg: 9 }}
      />
      <IndicatorsPeriodFilter
        globalPeriod={globalPeriod}
        onGlobalPeriodChange={handleGlobalPeriodChange}
        globalCustomDateRange={globalCustomDateRange}
        onGlobalDateRangeChange={handleGlobalDateRangeChange}
        onApplyGlobalCustomDateRange={applyGlobalCustomDateRange}
      />
      <IndicatorsAdoption
        condominiumId={selectedCondominium}
        startDate={formatDateParam(globalDateRange.since)}
        endDate={formatDateParam(globalDateRange.to)}
      />
      <h5 className="mb-3">Actividad por módulo</h5>
      <IndicatorsStats
        statsLoading={statsLoading}
        statsError={statsError}
        statsEmpty={statsEmpty}
        totals={totals}
        selectedMetric={selectedMetric}
        onSelectMetric={setSelectedMetric}
      />
      <IndicatorsTable
        data={tableRows}
        tableData={tableData}
        error={tableError}
        isLoading={tableLoading}
        meta={tableMeta}
        nextPage={nextPage}
        previousPage={previousPage}
        perPage={perPage}
        goToPage={goToPage}
        recordFilter={recordFilter}
        setRecordFilter={setRecordFilter}
        textSearch={textSearch}
        setTextSearch={setTextSearch}
        applyTextSearch={applyTextSearch}
        showDateFilter={showDateFilter}
        setShowDateFilter={setShowDateFilter}
        tableDateRange={tableDateRange}
        setTableDateRange={setTableDateRange}
        onTableDateRangeChange={handleTableDateRangeChange}
        applyFilters={applyFilters}
        resetTableDateFromGlobal={resetTableDateFromGlobal}
        selectedMetric={selectedMetric}
        selectedMetricLabel={selectedMetric ? METRIC_LABELS[selectedMetric] : ''}
        totalsByCondominium={totalsByCondominium}
        totalsByCondominiumLoading={false}
        tableRangeSource={tableRangeSource}
        scopeBannerText={scopeBannerText}
        tableDateValidationMessage={tableDateValidationMessage}
      />
    </>
  );
};

export default Indicators;